import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { toSignal } from '@angular/core/rxjs-interop';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import { VehicleStore } from '@modules/core/store/vehicle.store';
import { TranslateService } from '@modules/core/services/translate.service';
import { TranslateModule } from '@ngx-translate/core';
import { BaseWidgetDirective } from '@components/widgets/widgetsbase.directive';
import { VEHICLE_COLOR_RGB } from '@modules/shared/models/vehicle/vehicle-icons-enum';

export interface PunctualityCategory {
    color: string;
    label: string;
    count: number;
    pct: number;
    cssClass: string;
    zone: 'above' | 'below' | 'hidden';
}

const CATEGORY_ORDER: string[] = [
    VEHICLE_COLOR_RGB.Red,
    VEHICLE_COLOR_RGB.Green,
    VEHICLE_COLOR_RGB.Yellow,
    VEHICLE_COLOR_RGB.Orange,
    VEHICLE_COLOR_RGB.Pink,
    VEHICLE_COLOR_RGB.Gray,
];

@Component({
    selector: 'app-punctuality-widget',        
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, BaseWidgetDirective, DragDropModule],
    templateUrl: './punctuality-widget.component.html',
    styleUrls: ['./punctuality-widget.component.scss'],
})
export class PunctualityWidgetComponent {
    private readonly _ts = inject(TranslateService);
    private readonly _vehicleStore = inject(VehicleStore);

    public readonly widgetTitle = signal<string>('');
    public readonly widgetId = signal<string>('widget-punctuality');
    public readonly fillRemaining = signal<boolean>(false);
    public readonly fixedSize = signal<boolean>(true);
    public readonly defaultHeight = signal<number>(97);
    public readonly defaultFraction = signal<number>(0.1);

    public readonly categories = computed<PunctualityCategory[]>(() => {
        const vehicles = this._vehicles();
        if (vehicles == undefined) return undefined;

        const relevant = vehicles.filter(v => v.departureDeviation || (v.routeNumber && v.tripNumber && v.onTomLayOvr === false));
        const total = relevant.length;
        if (total === 0) return [];

        const counts = new Map<string, number>();
        for (const v of relevant) {
            const c = v.departureDeviation ? v.departureDeviation.color : VEHICLE_COLOR_RGB.Green;
            counts.set(c, (counts.get(c) ?? 0) + 1);
        }

        const labelMap: Record<string, string> = {
            [VEHICLE_COLOR_RGB.Red]:    this._ts.instant(_translate('Early')),
            [VEHICLE_COLOR_RGB.Green]:  this._ts.instant(_translate('On time')),
            [VEHICLE_COLOR_RGB.Yellow]: this._ts.instant(_translate('Late')),
            [VEHICLE_COLOR_RGB.Orange]: this._ts.instant(_translate('More late')),
            [VEHICLE_COLOR_RGB.Pink]:   this._ts.instant(_translate('Most late')),
            [VEHICLE_COLOR_RGB.Gray]:   this._ts.instant(_translate('Unknown')),
        };

        const metaMap: Record<string, { cssClass: string; zone: 'above' | 'below' | 'hidden' }> = {
            [VEHICLE_COLOR_RGB.Red]:    { cssClass: 'bar-red',    zone: 'below' },
            [VEHICLE_COLOR_RGB.Green]:  { cssClass: 'bar-green',  zone: 'above' },
            [VEHICLE_COLOR_RGB.Yellow]: { cssClass: 'bar-yellow', zone: 'above' },
            [VEHICLE_COLOR_RGB.Orange]: { cssClass: 'bar-orange', zone: 'above' },
            [VEHICLE_COLOR_RGB.Pink]:   { cssClass: 'bar-pink',   zone: 'above' },
            [VEHICLE_COLOR_RGB.Gray]:   { cssClass: '',           zone: 'hidden' },
        };

        return CATEGORY_ORDER
            .filter(color => (counts.get(color) ?? 0) > 0)
            .map(color => ({
                color,
                label: labelMap[color] ?? color,
                count: counts.get(color) ?? 0,
                pct: ((counts.get(color) ?? 0) / total) * 100,
                cssClass: metaMap[color]?.cssClass ?? '',
                zone: metaMap[color]?.zone ?? 'above',
            }));
    });

    public readonly isLoaded = computed(() => this.categories() != undefined);
    public readonly hasData  = computed(() => (this.categories()?.length ?? 0) > 0);

    protected tooltipText    = signal<string>('');
    protected tooltipX       = signal<number>(0);
    protected tooltipY       = signal<number>(0);
    protected tooltipVisible = signal<boolean>(false);

    private readonly _vehicles = toSignal(
        this._vehicleStore.getVehiclesState(),
        { initialValue: undefined }
    );

    constructor() {
        this.widgetTitle.set(this._ts.instant(_translate('Punctuality')));
    }

    protected onBarEnter(cat: PunctualityCategory, event: MouseEvent): void {
        this.tooltipText.set(`${cat.label}: ${cat.count}`);
        this.tooltipVisible.set(true);
        this.tooltipX.set(event.clientX);
        this.tooltipY.set(event.clientY);
    }

    protected onBarMove(event: MouseEvent): void {
        this.tooltipX.set(event.clientX);
        this.tooltipY.set(event.clientY);
    }

    protected onBarLeave(): void {
        this.tooltipVisible.set(false);
    }
}
